import formatCurrency from 'core-lib/utils/currency';
import dayjs from 'dayjs';
import { Car, HubDetails } from '../types/Car';
import { DealerDetails, DealerStore } from '../types/Dealer';
import {
  handleStoreImage,
  modifyOwnerNum,
  numOfDays,
  processUcdImageKit,
} from './helpers';

const findImage = (exteriorImages) => {
  let url =
    exteriorImages?.find(
      (el: any) =>
        el.subcategory_type === 'FRONT_VIEW' || el.priority_order === 1
    )?.images_url?.category_image?.image_url ||
    exteriorImages[0]?.images_url?.category_image?.image_url;
  if (url) {
    return processUcdImageKit(url);
  } else {
    // eslint-disable-next-line no-restricted-syntax
    for (const i in exteriorImages) {
      if (
        exteriorImages[i]?.['images_url']?.['category_image']?.['image_url']
      ) {
        url =
          exteriorImages[i]?.['images_url']?.['category_image']?.['image_url'];
        return processUcdImageKit(url);
      }
    }
  }

  // placeholder image
  return processUcdImageKit(
    'https://parkplus-bkt-img.parkplus.io/production/team/public/get_500_20240424122538547468.jpeg'
  );
};

export function dealerDetailsSerializer(data: any): DealerDetails {
  return {
    name: data?.name || data?.company_trade_name,
    phone: data?.phone || data?.phone_number,
    whatsappNo: data?.whatsapp_number || data?.phone || data?.phone_number,
    address: data?.address || data?.dealer_address,
    businessHours: {
      startTime: data?.business_hours?.startTime || data?.work_start_time,
      endTime: data?.business_hours?.endTime ?? data?.work_end_time,
    },
    storeImg: handleStoreImage(data?.logo || data?.store_image),
    updatedAgo: data?.updated ? numOfDays(data?.updated) : 'N/A',
    dealerId: data?.dealer_id,
    distance: data?.distance_from_curr_loc,
    latitude: data?.latitude,
    longitude: data?.longitude,
    isVerified: data?.is_subscribed ?? false,
    partnerCode: data?.partner_code,
  };
}

export function carHighlightsSerializer(data: any): any {
  return {
    makeYear: data?.make_year ?? 'N/A',
    fuelType: data?.fuel_type ?? 'N/A',
    engineDisplacement: data?.engine ?? 'N/A',
    transmission: data?.transmission ?? 'N/A',
    maxPower: data?.max_power ?? 'N/A',
    fuelTankCapacity: data?.fuel_tank_capacity ?? 'N/A',
    maxTorque: data?.max_torque ?? 'N/A',
    cityMileage: data?.mileage ?? 'N/A',
    warranty: data?.waranty ?? 'N/A',
    seats: data?.seating_capacity ?? 'N/A',
    groundClearance: data?.ground_clearance ?? 'N/A',
    bodyType: data?.body_type ?? 'N/A',
  };
}

export function getHubDetails(data: any): HubDetails {
  const hubData: HubDetails = {
    address: data?.address,
    cityName: data?.city_name,
    code: data?.code,
    mapLink: data?.mapLink,
    name: data?.name,
    pincode: data?.pincode,
    storeImage: data?.store_image,
  };
  return hubData;
}

export function inventorySerializer(data: any): Car {
  const exteriorImages = data?.exterior ?? data?.exterior_images;
  return {
    isLeadGenerated: data?.is_lead_generated ?? false,
    partnerCode: data?.partner_code || null,
    badge: data?.badge,
    viewed: data?.viewed,
    d2cClicks: data?.d2c_clicks,
    createdAt: data?.created,
    favourite: data?.favourite,
    damageImages: null,
    status: data?.inventory_status,
    video: null,
    inventoryId: data?.inventory_id ?? null,
    brandId: data?.brand_id ?? null,
    modelId: data?.model_id ?? null,
    variantId: data?.variant_id ?? null,
    brandName: data?.brand_name ?? 'N/A',
    modelName: data?.model_name ?? 'N/A',
    variantName: data?.variant_name ?? 'N/A',
    views: data?.views ?? 'N/A',
    transmission:
      (data?.specification?.transmission_type || data?.transmission_type) ??
      'N/A',
    ownerNumber: modifyOwnerNum(data?.owner_no) ?? 'N/A',
    kmDriven: data?.km_driven ?? 'N/A',
    manufacturingYear: data?.manufacturing_year ?? 'N/A',
    regDate: data?.reg_date || data?.registration_year || 'N/A',
    emi: data?.emi,
    fuelType: data?.fuel_type ?? 'N/A',
    bodyType: data?.body_type ?? 'N/A',
    vrn: data?.vrn ?? 'N/A',
    rtoCode: data?.rto ?? 'N/A',
    hubDetails: getHubDetails(data?.hub),
    insuranceType: data?.insurance_type ?? 'N/A',
    insuranceValidTill: data?.insurance_validity ?? 'N/A',
    featureDescription: data?.feature_description ?? 'N/A',
    warrantyDate: data?.warranty ?? 'N/A',
    updated: data?.updated ?? 'N/A',
    interiorImage:
      data?.interior?.map((image: any) => ({
        url: processUcdImageKit(image?.images_url?.category_image?.image_url),
        viewType: image?.subcategory_type,
      })) ?? [],
    exteriorImage:
      exteriorImages?.map((image: any) => ({
        url: processUcdImageKit(
          image?.images_url?.category_image?.image_url ?? ''
        ),
        viewType: image?.subcategory_type ?? '',
      })) ?? [],
    frontImage: findImage(exteriorImages),
    price: data?.price ? formatCurrency(data?.price) : '-',
    offerPrice: data?.discount_price
      ? formatCurrency(data?.discount_price)
      : null,
    isNegotiable: data?.is_negotiable ?? false,
    serviceLink: data?.service?.link ?? null,
    highlights: carHighlightsSerializer(data?.highlights),
    dealerDetails: dealerDetailsSerializer(data?.dealer_details),
    dealerId: data?.dealer_id,
    similarCars:
      data?.similar_cars?.map((ele: any) => inventorySerializer(ele)) || [],
    isVerified: data.badge ? true : false,
    postedOn: dayjs(data?.created).format("DD MMM 'YY"),
  };
}

export function dealerStoreSerializer(data: any): DealerStore {
  const result = {
    dealerDetails: dealerDetailsSerializer(data?.dealer_details),
    dealerId: data?.dealer_details?.dealer_id ?? null,
    cars: data?.car_list.map((ele: any) => inventorySerializer(ele)) ?? [],
    inventoryCount: data?.inventory_count ?? 0,
  };
  result.cars = result.cars?.map((car) => ({
    ...car,
    dealerDetails: {
      ...car?.dealerDetails,
      whatsappNo:
        car?.dealerDetails.whatsapp || data?.dealer_details?.whatsapp_number,
      phone: car?.dealerDetails.phone || data?.dealer_details?.phone_number,
    },
  }));
  return result;
}
